import { ComponentCO2 } from '@acies/domain';
import { FormInputEvent } from '@acies/ui';
import NetworkError from '@shared/errors/network-error';
import CO2Model from '../models/co2-model';
import EditComponentCO2Model from '../models/edit-component-co2-model';
import ListComponentCO2Model from '../models/list-component-co2-model';
import { numberToScientific } from '../models/utils';
import CO2Service from '../services/co2-service';

export default class EditComponentCO2Controller {
	public set selectedComponentCO2(value: ComponentCO2 | null) {
		if (this.#selectedComponentCO2 === value) {
			return;
		}
		this.#selectedComponentCO2 = value;
		this.#selectedComponentCO2Changed();
	}

	public get selectedComponentCO2(): ComponentCO2 | null {
		return this.#selectedComponentCO2;
	}

	public dialog: HTMLDialogElement | null;
	#selectedComponentCO2: ComponentCO2 | null;
	#editComponentCO2Model: EditComponentCO2Model;
	#listComponentCO2Model: ListComponentCO2Model;
	#co2Service: CO2Service;
	#co2Model: CO2Model;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.dialog = null;
		this.#co2Model = co2Model;
		this.#editComponentCO2Model = co2Model.editComponentCO2Model;
		this.#listComponentCO2Model = co2Model.listComponentCO2Model;
		this.#co2Service = co2Service;
	}

	/** When FormInput components in the Edit Dialog dispatches events, they are forwarded to this method. Angular expects the Event type as parameter in event handlers, so we check for the event being an instance of the FormInputEvent to get full type safety and the TSC compiler happy :) */
	public formInputEvent(event: Event): void {
		if (event instanceof FormInputEvent) {
			this.#updateEditComponentCO2ModelFromEvent(event);
			if (event.type === FormInputEvent.NOT_VALID_VALUE_CHANGED) {
				this.#editComponentCO2Model.updateButtonEnabled = false;
			}
		}
	}

	public showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
			this.#hideAllErrorLabels();
			this.#editComponentCO2Model.resetUIBindings();
			this.#updateEditComponentCO2ModelFromSelectedComponentCO2();
		}
	}

	public updateButtonClicked(): void {
		this.#editComponentCO2Model.editSpinnerVisible = true;
		const version = this.#selectedComponentCO2.version;
		this.#co2Service.updateComponentCO2(this.#editComponentCO2Model.updateComponentCO2Body, version).subscribe((response) => {
			if (response instanceof ComponentCO2) {
				// We clear and reload the ComponentCO2's here.
				// We could remove the current selected ComponentCO2 instance,
				// and replace it with the newly edited one, but the
				// ObservableCollection does not have this functionality
				// yet at this time 13 jan 2026.
				this.#listComponentCO2Model.co2Collection.clear();
				// Note: when we clear the co2Collection, a reset callback
				// is invoked in the SelectList component, that will trigger
				// a dispatch of the SelectListEvent with null as selectedItem.
				// This is cool because it insures that the logic in the other
				// controllers works as expected.
				this.#loadComponentsCO2();
			} else if (response === null) {
				// if the response is null, nothing was updated on the server.
				// We can safely close without loading the component-co2's again.
				this.#editComponentCO2Model.editSpinnerVisible = false;
				this.closeDialog();
			} else {
				this.#editComponentCO2Model.editSpinnerVisible = false;
				this.#showErrorLabel(response);
			}
		});
	}

	#hideAllErrorLabels(): void {
		this.#editComponentCO2Model.networkErrorLabelVisible = false;
		this.#editComponentCO2Model.unknownErrorLabelVisible = false;
	}

	#showErrorLabel(error: NetworkError): void {
		if (error instanceof NetworkError) {
			this.#editComponentCO2Model.networkErrorLabelVisible = true;
		} else {
			this.#editComponentCO2Model.unknownErrorLabelVisible = true;
		}
	}

	#loadComponentsCO2(): void {
		this.#co2Service.listComponentCO2().subscribe((response) => {
			this.#editComponentCO2Model.editSpinnerVisible = false;
			if (Array.isArray(response)) {
				this.#listComponentCO2Model.co2Collection.addItems(response);
				this.closeDialog();
			} else {
				this.#co2Model.showGlobalErrorDialog(response);
			}
		});
	}

	#selectedComponentCO2Changed(): void {
		if (this.#selectedComponentCO2 instanceof ComponentCO2) {
			this.#editComponentCO2Model.editButtonEnabled = true;
			this.#updateEditComponentCO2ModelFromSelectedComponentCO2();
		} else {
			this.#editComponentCO2Model.editButtonEnabled = false;
			this.#editComponentCO2Model.resetUIBindings();
		}
	}

	#updateEditComponentCO2ModelFromSelectedComponentCO2(): void {
		if (this.#selectedComponentCO2) {
			const { epd, name, description, phases } = this.#selectedComponentCO2;
			const { a1, a2, a3, a4, a5, c1, c2, c3, c4, d } = phases;
			this.#editComponentCO2Model.epd = epd;
			this.#editComponentCO2Model.name = name;
			this.#editComponentCO2Model.description = description;
			this.#editComponentCO2Model.a1 = numberToScientific(a1);
			this.#editComponentCO2Model.a2 = numberToScientific(a2);
			this.#editComponentCO2Model.a3 = numberToScientific(a3);
			this.#editComponentCO2Model.a4 = numberToScientific(a4);
			this.#editComponentCO2Model.a5 = numberToScientific(a5);
			this.#editComponentCO2Model.c1 = numberToScientific(c1);
			this.#editComponentCO2Model.c2 = numberToScientific(c2);
			this.#editComponentCO2Model.c3 = numberToScientific(c3);
			this.#editComponentCO2Model.c4 = numberToScientific(c4);
			this.#editComponentCO2Model.d = numberToScientific(d);
		}
	}

	#updateEditComponentCO2ModelFromEvent(event: FormInputEvent): void {
		switch (event.key) {
			case 'epd':
				this.#editComponentCO2Model.epd = event.value;
				break;
			case 'name':
				this.#editComponentCO2Model.name = event.value;
				break;
			case 'description':
				this.#editComponentCO2Model.description = event.value;
				break;
			case 'a1':
				this.#editComponentCO2Model.a1 = event.value;
				break;
			case 'a2':
				this.#editComponentCO2Model.a2 = event.value;
				break;
			case 'a3':
				this.#editComponentCO2Model.a3 = event.value;
				break;
			case 'a4':
				this.#editComponentCO2Model.a4 = event.value;
				break;
			case 'a5':
				this.#editComponentCO2Model.a5 = event.value;
				break;
			case 'c1':
				this.#editComponentCO2Model.c1 = event.value;
				break;
			case 'c2':
				this.#editComponentCO2Model.c2 = event.value;
				break;
			case 'c3':
				this.#editComponentCO2Model.c3 = event.value;
				break;
			case 'c4':
				this.#editComponentCO2Model.c4 = event.value;
				break;
			case 'd':
				this.#editComponentCO2Model.d = event.value;
				break;
			default:
				break;
		}
		this.#updateEditComponentCO2IsValid();
	}

	#updateEditComponentCO2IsValid(): void {
		this.#editComponentCO2Model.updateButtonEnabled = this.#editComponentCO2Model.epd !== '';
	}
}
